import { Observable } from 'rxjs';
import { inject, Injectable } from '@angular/core';

import { ISimpleItem } from '@core/models/simple-item.model';
import { ILocationParameter } from '@core/models/parameters.model';
import { DoodLocationParameters } from '@store/settings/settings.model';

import { DestroyerBase } from '@core/base/destroyer/destroyer.base';
import { SettingsParametersState } from '@store/settings/settings.state';
import { SettingsStoreSelector } from '@common/selectors/settings.selector';
import { OrderTypeService } from '@core/services/order-type/order-type.service';
import { ParametersWantedAtTypeLabels } from '@config/labels/parameters.labels';

import { DistributionModeValues, OrderTypeValues } from '@config/values/order.values';
import { ParametersDistributionModeValues } from '@config/values/parameters.values';
import { SettingsStoreDispatcher } from '@common/dispatchers/settings.dispatcher';
import { TranslateService } from '@ngx-translate/core';
import dayjs from 'dayjs';
import isTomorrow from 'dayjs/plugin/isTomorrow';
import { DateUtils } from '@shared/utils/date/date.utils';

@Injectable({
  providedIn: 'root',
})
export class ShopSearchParametersService extends DestroyerBase {
  private settingsSelector = inject(SettingsStoreSelector);
  private settingsDispatcher = inject(SettingsStoreDispatcher);
  private readonly orderTypeService = inject(OrderTypeService);
  private readonly translateService = inject(TranslateService);

  constructor() {
    super();
    dayjs.extend(isTomorrow);
  }

  set(parameters: Partial<SettingsParametersState>): void {
    parameters.last_update = new Date();
    this.settingsDispatcher.updateParameters(parameters);
  }

  setLocation(location?: ILocationParameter): void {
    this.set({
      location,
      is_default_location: false,
    });
  }

  setLocationFromQueryParams(locationParameters: DoodLocationParameters): void {
    this.set({
      location: {
        address: `${locationParameters.deliveryStreetAddress}, ${locationParameters.deliveryPostalCode} ${locationParameters.deliveryCity}, ${locationParameters.deliveryCountry}`,
        lat: Number(locationParameters.deliveryLat),
        lng: Number(locationParameters.deliveryLng),
      },
      location_params: {
        deliveryStreetAddress: `${locationParameters.deliveryStreetAddress}`,
        deliveryPostalCode: `${locationParameters.deliveryPostalCode}`,
        deliveryCity: `${locationParameters.deliveryCity}`,
        deliveryCountry: `${locationParameters.deliveryCountry}`,
        deliveryLat: Number(locationParameters.deliveryLat),
        deliveryLng: Number(locationParameters.deliveryLng),
        deliveryInstructions: `${locationParameters.deliveryInstructions}`,
      },
    });
  }

  setDistributionModeType(distributionMode: string, isDistributionModeDefinedByUser = true): void {
    if (distributionMode === ParametersDistributionModeValues.Delivery) {
      this.set({
        wanted_at: null,
      });
    }
    this.set({
      distribution_mode: distributionMode,
      is_distribution_mode_defined: isDistributionModeDefinedByUser,
    });
  }

  /**
   * Explicit user choice (mosaic card, cart parameters modal, etc.).
   * Clears a QR / URL force lock so other distribution modes become available.
   */
  applyUserSelectedDistributionMode(distributionMode: string): void {
    this.setDistributionModeType(distributionMode, true);
    this.setForceDistributionMode(null);
  }

  getParameters$(): Observable<SettingsParametersState> {
    return this.settingsSelector.selectParameters$;
  }

  getParameters(): SettingsParametersState {
    return this.settingsSelector.parameters;
  }

  getDistributionMode(): string {
    const parameters = this.getParameters();
    return parameters.distribution_mode;
  }

  retrieveParametersValue(parameters: SettingsParametersState): ISimpleItem[] {
    const distributionMode = parameters.distribution_mode;
    const orderTypeCapabilities = this.orderTypeService.getCapabilities(distributionMode);
    const distributionLabel = orderTypeCapabilities.label;
    const distributionIcon = orderTypeCapabilities.icon;
    const items = [];

    items.push({
      label: distributionLabel as string,
      icon: distributionIcon,
      value: distributionMode,
    });

    if (orderTypeCapabilities.preorderingAllowed) {
      const wantedAtTypeLabel = parameters.wanted_at
        ? this.getDateText(parameters.wanted_at, distributionMode as OrderTypeValues)
        : ParametersWantedAtTypeLabels.ASAP;
      items.push({
        label: wantedAtTypeLabel as string,
        icon: 'icon-clock',
        value: wantedAtTypeLabel as string,
      });
    }

    return items;
  }

  private getDateText(wantedAt: Date, distributionModeType: OrderTypeValues): string {
    const formattedDate = dayjs(new Date(wantedAt));
    if (formattedDate.isTomorrow()) {
      return this.translateService.instant('parameters.wantedAt.tomorrow');
    } else {
      const dateTextFinal = DateUtils.formatLocalizedDate(
        formattedDate.toDate(),
        this.translateService.instant('date.day-month-format'),
      );
      const deltaDisplayed = distributionModeType === OrderTypeValues.Delivery ? 900 * 1000 : 0;
      const dateDisplayed = new Date(new Date(wantedAt).getTime() + deltaDisplayed);

      return `<span class="block font-base text-sm">${dateTextFinal}</span> <span class="block font-base text-sm">${this.getReadyAtLabel(dateDisplayed)}</span>`;
    }
  }

  private getReadyAtLabel(readyAt: Date): string | undefined {
    const minutes = DateUtils.dayjsInMarketplaceTimezone(readyAt).minute();
    const localizedHourFormat = this.translateService.instant('parameters.wantedAt.slots-format');
    if (minutes >= 0 && minutes < 15) {
      return `${DateUtils.dayjsInMarketplaceTimezone(readyAt).minute(0).format(localizedHourFormat)} - ${DateUtils.dayjsInMarketplaceTimezone(readyAt).minute(15).format(localizedHourFormat)}`;
    } else if (minutes >= 15 && minutes < 30) {
      return `${DateUtils.dayjsInMarketplaceTimezone(readyAt).minute(15).format(localizedHourFormat)} - ${DateUtils.dayjsInMarketplaceTimezone(readyAt).minute(30).format(localizedHourFormat)}`;
    } else if (minutes >= 30 && minutes < 45) {
      return `${DateUtils.dayjsInMarketplaceTimezone(readyAt).minute(30).format(localizedHourFormat)} - ${DateUtils.dayjsInMarketplaceTimezone(readyAt).minute(45).format(localizedHourFormat)}`;
    } else if (minutes >= 45 && minutes < 60) {
      return `${DateUtils.dayjsInMarketplaceTimezone(readyAt).minute(45).format(localizedHourFormat)} - ${DateUtils.dayjsInMarketplaceTimezone(
        readyAt,
      )
        .hour(DateUtils.dayjsInMarketplaceTimezone(readyAt).hour() + 1)
        .minute(0)
        .format(localizedHourFormat)}`;
    }
  }

  setDefinedDistributionModeType(is_distribution_mode_defined: boolean): void {
    this.set({ is_distribution_mode_defined });
  }

  setDefault(defaultValues: any): void {
    if (
      defaultValues.latitude &&
      defaultValues.longitude &&
      (!this.settingsSelector.parameters?.location?.lat ||
        !this.settingsSelector.parameters?.location?.lng)
    ) {
      this.set({
        location: {
          lat: defaultValues.latitude,
          lng: defaultValues.longitude,
        },
        is_default_location: true,
      });
    }
    if (defaultValues.maxDistance && !this.settingsSelector.parameters?.distance) {
      this.set({ distance: defaultValues.maxDistance });
    }
  }

  isDeliveryDistributionMode(type: string): boolean {
    switch (type) {
      case DistributionModeValues.Stuart:
      case DistributionModeValues.UberDirect:
      case DistributionModeValues.DeliverectDispatch:
      case DistributionModeValues.Shipday:
      case DistributionModeValues.ZitiCity:
      case DistributionModeValues.OwnDelivery:
        return true;
      default:
        return false;
    }
  }

  setForceDistributionMode(force_distribution_mode?: string | null): void {
    this.set({ force_distribution_mode });
  }
}
